import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Download, Loader2, X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { getIndustryBySlug, industries } from '../../data/industries';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const IndustryDetail = () => {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    designation: '',
    consent: false
  });

  if (!industry) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Industry not found</h1>
          <Link to="/industries" className="text-orange-500 hover:underline">
            Back to Industries
          </Link>
        </div>
      </div>
    );
  }

  const Icon = LucideIcons[industry.icon];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.designation) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.consent) {
      toast.error('Please agree to receive communications');
      return;
    }

    setIsSubmitting(true);

    // Mock email sending - simulating API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Log the mock email for demonstration
      console.log('=== MOCK EMAIL SENT ===');
      console.log('To:', formData.email);
      console.log('Subject: Your TradeEngine.AI Playbook - ' + industry.playbookTitle);
      console.log('Body: Thank you for your interest! Here is your playbook download link.');
      console.log('Attachment: ' + industry.slug + '-playbook.pdf');
      console.log('========================');
      
      toast.success('Check your email for the playbook download link!');
    }, 2000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      designation: '',
      consent: false
    });
    setIsSubmitted(false);
    setIsModalOpen(false);
  };

  // Get related industries (excluding current)
  const relatedIndustries = industries.filter(ind => ind.id !== industry.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Breadcrumb */}
            <Link 
              to="/industries" 
              className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Industries
            </Link>

            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                {Icon && <Icon className="w-8 h-8 text-orange-500" />}
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  {industry.title}
                </h1>
                <p className="text-lg text-slate-400 max-w-2xl">
                  {industry.heroDescription}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 lg:py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  {industry.writeup.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Benefits */}
                <div className="mt-12 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    Key Benefits
                  </h3>
                  <ul className="space-y-3">
                    {industry.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <span className="text-slate-700 dark:text-slate-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sidebar - Playbook CTA */}
              <div className="lg:col-span-1">
                <div className="sticky top-28 bg-slate-900 dark:bg-slate-800 rounded-xl p-6 lg:p-8">
                  <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center mb-4">
                    <Download className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Free Playbook
                  </h3>
                  <h4 className="text-lg text-orange-400 mb-4">
                    {industry.playbookTitle}
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">
                    {industry.playbookDescription}
                  </p>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full bg-white text-slate-900 hover:bg-slate-100"
                  >
                    Download Playbook
                    <Download className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Industries */}
        <section className="py-16 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">
              Explore Other Industries
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedIndustries.map((ind) => {
                const RelatedIcon = LucideIcons[ind.icon];
                return (
                  <Link
                    key={ind.id}
                    to={`/industries/${ind.slug}`}
                    className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 hover:border-slate-300 dark:hover:border-slate-700 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        {RelatedIcon && <RelatedIcon className="w-5 h-5 text-slate-600 dark:text-slate-400 group-hover:text-orange-500 transition-colors" />}
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-orange-500 transition-colors">
                        {ind.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {ind.shortDescription}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Download Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={() => !isSubmitting && resetForm()}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md p-6 lg:p-8">
            {/* Close Button */}
            <button
              onClick={() => !isSubmitting && resetForm()}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Check Your Email!
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  We've sent the playbook download link to <strong>{formData.email}</strong>
                </p>
                <Button onClick={resetForm} variant="outline" className="w-full">
                  Close
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                    Download Free Playbook
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {industry.playbookTitle}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 text-sm">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 text-sm">
                      Work Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 dark:text-slate-300 text-sm">
                      Contact Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+65 9123 4567"
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-700 dark:text-slate-300 text-sm">
                      Company *
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name"
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="designation" className="text-slate-700 dark:text-slate-300 text-sm">
                      Designation *
                    </Label>
                    <Input
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="Head of Operations"
                      className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      required
                    />
                  </div>

                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed cursor-pointer">
                      I agree to receive communications from TradeEngine.AI about products, services, and industry insights.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 dark:bg-white dark:text-slate-900 text-white hover:bg-slate-800 dark:hover:bg-slate-100 mt-4"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Get Playbook
                        <Download className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustryDetail;
